import { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { toastApiError } from "@/lib/toastApiError";
import type { Term, AdminStats } from "../types";
import {
  createTermMultipart,
  deleteTermRemote,
  fetchTermByIdRemote,
  fetchTermsRemote,
  termToMultipartFields,
  updateTermMultipart,
  type TermMultipartFields,
} from "@/lib/termsApi";

/** Fields sent via multipart PATCH/POST (`admin_notes` when supported by API). */
export type TermMultipartSave = Partial<TermMultipartFields> & {
  admin_notes?: string;
};

export function useAdminTerms(isAdmin: boolean) {
  const { staffRole, isLexiconStaffAdmin } = useAuth();
  /** Only Lexicon admins may delete terms; scholars may edit/review only. */
  const canDeleteTerms =
    staffRole === "admin" || isLexiconStaffAdmin();

  const [terms, setTerms] = useState<Term[]>([]);
  const [isLoadingTerms, setIsLoadingTerms] = useState(true);

  const fetchTermDetail = useCallback(async (termId: string) => {
    return fetchTermByIdRemote(termId, "staff");
  }, []);

  const fetchTerms = useCallback(async () => {
    setIsLoadingTerms(true);
    try {
      const { terms: rows, error } = await fetchTermsRemote("staff");
      if (error) {
        toastApiError(`Failed to load terms: ${error}`);
        console.error(error);
      } else {
        setTerms([...rows].sort(compareTermsByWordAsc));
      }
    } catch (err) {
      console.error("Fetch terms error:", err);
      toastApiError("Network error fetching terms");
    } finally {
      setIsLoadingTerms(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchTerms();
    }
  }, [isAdmin, fetchTerms]);

  const stats: AdminStats = useMemo(
    () => ({
      pending: terms.filter(t => t.status === "pending").length,
      approved: terms.filter(t => t.status === "approved").length,
      rejected: terms.filter(t => t.status === "rejected").length,
    }),
    [terms]
  );

  const alphabetCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    terms
      .filter(t => t.status === "approved")
      .forEach(term => {
        const letter = term.word.charAt(0).toUpperCase();
        counts[letter] = (counts[letter] || 0) + 1;
      });
    return Object.entries(counts).sort(([a], [b]) => a.localeCompare(b));
  }, [terms]);

  const recentTermsByMonth = useMemo(() => {
    const months: Record<string, number> = {};
    terms.forEach(term => {
      const month = new Date(term.created_at).toLocaleDateString("en-US", {
        month: "short",
        year: "2-digit",
      });
      months[month] = (months[month] || 0) + 1;
    });
    return Object.entries(months)
      .slice(-6)
      .map(([month, count]) => ({ month, count }));
  }, [terms]);

  const handleApprove = async (term: Term) => {
    const { ok, error } = await updateTermMultipart(term.id, {
      ...termToMultipartFields({ ...term, status: "approved" }),
      admin_notes: term.admin_notes ?? "",
    });
    if (!ok || error) {
      toastApiError(error || "Failed to approve term");
    } else {
      toast.success(`"${term.word}" approved!`);
      fetchTerms();
    }
  };

  const handleReject = async (term: Term) => {
    const { ok, error } = await updateTermMultipart(term.id, {
      ...termToMultipartFields({ ...term, status: "rejected" }),
      admin_notes: term.admin_notes ?? "",
    });
    if (!ok || error) {
      toastApiError(error || "Failed to reject term");
    } else {
      toast.success(`"${term.word}" rejected.`);
      fetchTerms();
    }
  };

  const handleDelete = async (term: Term) => {
    if (!canDeleteTerms) {
      toastApiError("You don't have access to delete terms.");
      return;
    }
    const { ok, error } = await deleteTermRemote(term.id);
    if (!ok || error) {
      toastApiError(error || "Failed to delete term");
    } else {
      toast.success("Term deleted!");
      fetchTerms();
    }
  };

  const handleEdit = async (
    id: string,
    data: TermMultipartSave,
    audioFile?: File | null
  ) => {
    const { ok, error } = await updateTermMultipart(id, data, audioFile);
    if (!ok || error) {
      toastApiError(error || "Failed to update term");
    } else {
      toast.success("Term updated!");
      fetchTerms();
    }
  };

  const handleAdd = async (
    data: TermMultipartSave,
    audioFile?: File | null
  ) => {
    const { ok, error } = await createTermMultipart(data, audioFile, "staff");
    if (!ok || error) {
      toastApiError(error || "Failed to add term");
    } else {
      toast.success("Term added!");
      fetchTerms();
    }
  };

  return {
    terms,
    isLoadingTerms,
    stats,
    alphabetCounts,
    recentTermsByMonth,
    fetchTerms,
    fetchTermDetail,
    handleApprove,
    handleReject,
    handleDelete,
    handleEdit,
    handleAdd,
    canDeleteTerms,
  };
}

/** Alphabetical A→Z for admin lists (Word column). */
function compareTermsByWordAsc(a: Term, b: Term): number {
  const wa = (a.word ?? "").trim();
  const wb = (b.word ?? "").trim();
  const byWord = wa.localeCompare(wb, undefined, { sensitivity: "base" });
  if (byWord !== 0) {
    return byWord;
  }
  const ta = Date.parse(a.created_at) || 0;
  const tb = Date.parse(b.created_at) || 0;
  return tb - ta;
}
